import React from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Grid
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface ExternalValidationEntry {
  type: 'press_review' | 'award' | 'festival_appearance' | 'radio_airplay' | 'other';
  source: string;
  quote?: string;
  author?: string;
  url?: string;
  date?: Date | null;
  featured?: boolean;
}

interface ExternalValidationProps {
  value: ExternalValidationEntry[];
  onChange: (entries: ExternalValidationEntry[]) => void;
}

const validationTypes = [
  { value: 'press_review', label: 'Press Review' },
  { value: 'award', label: 'Award' },
  { value: 'festival_appearance', label: 'Festival Appearance' },
  { value: 'radio_airplay', label: 'Radio/Media Play' },
  { value: 'other', label: 'Other' }
];

const ExternalValidation: React.FC<ExternalValidationProps> = ({ value, onChange }) => {
  const handleAdd = () => {
    onChange([
      ...value,
      {
        type: 'press_review',
        source: '',
        quote: '',
        author: '',
        url: '',
        date: null,
        featured: false
      }
    ]);
  };

  const handleRemove = (index: number) => {
    const updated = value.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleChange = (index: number, field: keyof ExternalValidationEntry, fieldValue: any) => {
    const updated = value.map((entry, i) => {
      if (i === index) {
        return { ...entry, [field]: fieldValue };
      }
      return entry;
    });
    onChange(updated);
  };

  const getQuoteLabel = (type: string) => {
    switch (type) {
      case 'award':
        return 'Award Title';
      case 'festival_appearance':
        return 'Festival Name & Details';
      case 'radio_airplay':
        return 'Show/Program Details';
      default:
        return 'Quote or Description';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h6" gutterBottom>
          Press & Awards
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Add press reviews, awards, festival appearances, and other external recognition to strengthen your profile.
        </Typography>

        {value.map((entry, index) => (
          <Card key={index} sx={{ mb: 2, position: 'relative' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Entry #{index + 1}
                </Typography>
                <IconButton
                  onClick={() => handleRemove(index)}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>

              <Grid container spacing={2}>
                {/* Type */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={entry.type}
                      label="Type"
                      onChange={(e) => handleChange(index, 'type', e.target.value)}
                    >
                      {validationTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Source */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Source"
                    placeholder="All About Jazz, Irish Times, BBC Radio 3..."
                    value={entry.source}
                    onChange={(e) => handleChange(index, 'source', e.target.value)}
                    helperText="Publication, organization, or media outlet"
                    inputProps={{ autoComplete: 'off' }}
                  />
                </Grid>

                {/* Quote/Title */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={getQuoteLabel(entry.type)}
                    placeholder={
                      entry.type === 'press_review'
                        ? '"A thrilling exploration of modern jazz harmony..."'
                        : entry.type === 'award'
                        ? 'Best Jazz Album 2024'
                        : 'Details...'
                    }
                    value={entry.quote || ''}
                    onChange={(e) => handleChange(index, 'quote', e.target.value)}
                    inputProps={{ autoComplete: 'off' }}
                  />
                </Grid>

                {/* Author (only show for press reviews) */}
                {entry.type === 'press_review' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Reviewer/Author"
                      placeholder="John Smith"
                      value={entry.author || ''}
                      onChange={(e) => handleChange(index, 'author', e.target.value)}
                      helperText="Optional"
                      inputProps={{ autoComplete: 'off' }}
                    />
                  </Grid>
                )}

                {/* URL */}
                <Grid item xs={12} md={entry.type === 'press_review' ? 6 : 8}>
                  <TextField
                    fullWidth
                    label="Source URL"
                    placeholder="https://..."
                    value={entry.url || ''}
                    onChange={(e) => handleChange(index, 'url', e.target.value)}
                    helperText="Link to full article or source"
                    inputProps={{ autoComplete: 'off' }}
                  />
                </Grid>

                {/* Date */}
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Date"
                    value={entry.date && !isNaN(new Date(entry.date).getTime()) ? new Date(entry.date) : null}
                    onChange={(newDate) => handleChange(index, 'date', newDate)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText: 'Optional'
                      }
                    }}
                  />
                </Grid>

                {/* Featured checkbox */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={entry.featured || false}
                        onChange={(e) => handleChange(index, 'featured', e.target.checked)}
                      />
                    }
                    label="Feature this prominently (show this quote/award in highlighted positions)"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          fullWidth
          sx={{ mt: 2 }}
        >
          Add Quote or Other Validation
        </Button>
      </Box>
    </LocalizationProvider>
  );
};

export default ExternalValidation;